import { useIsFocused } from "expo-router/react-navigation";
import { router, useLocalSearchParams } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect, useLayoutEffect } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useCreateManyPastors } from '@/api/hooks/pastorsHooks';
import { useNpoOnboarding } from '@/api/hooks/meHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import AddAnotherButton from '@/components/buttons/AddAnotherButton';
import BrandedButton from '@/components/buttons/BrandedButton';
import PastorForm from '@/components/forms/PastorForm';
import FormWrapper from '@/components/forms/FormWrapper';
import FieldArrayListItemCard from '@/components/npo/onboarding/FieldArrayListItemCard';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { PastorsData, pastorsResolver, PastorData } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { objectComparision } from '@/utils/objectComparision';

// Just for CHURCH type
const Pastors = () => {
  const { pastorDataJson, pastorIndex } = useLocalSearchParams<{
    pastorDataJson: string;
    pastorIndex: string;
  }>();
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useNpoOnboarding(
    authState.signUpData?.loginEmail?.type ?? 'CHURCH'
  );
  const { mutateAsync: createManyPastors, isPending: pastorsPending } = useCreateManyPastors();

  const { height } = useWindowDimensions();
  const { top, bottom } = useSafeAreaInsets();

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    // Only Churches
    if (isFocused) setCurrentPage(5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (pastorDataJson) {
      const pastorData = safeJsonParse<PastorData>(pastorDataJson);
      if (pastorData && fields !== undefined) {
        // eslint-disable-next-line no-unused-expressions
        !pastorIndex && !fields.find((field) => objectComparision(field, pastorData))
          ? append(pastorData)
          : update(+pastorIndex, pastorData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastorDataJson]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');

  const isIndividual = authState.authReady && authState.user?.user.role === 'INDIVIDUAL';

  const {
    control,
    handleSubmit,
    trigger,
    clearErrors,
    getValues,
    formState: { isValid, submitCount, errors }
  } = useForm<PastorsData>({
    defaultValues: {
      pastors:
        nonProfitOrg?.pastors !== undefined ? safeJsonParse<PastorData[]>(JSON.stringify(nonProfitOrg.pastors)) : []
    },
    mode: 'onSubmit',
    resolver: pastorsResolver,
    context: { isIndividual }
  });

  const { fields, insert, append, update, remove } = useFieldArray({
    control,
    name: 'pastors'
  });

  const onSubmit: SubmitHandler<PastorsData> = async (data) => {
    const mappedData = data.pastors.map((pastor) => ({
      ...pastor,
      nonProfitOrgId: authState.signUpData?.loginEmail?.npoId ?? ''
    }));
    const body = {
      pastors: mappedData,
      npoId: authState.signUpData?.loginEmail?.npoId
    };

    try {
      createManyPastors(body, {
        onSuccess: () => {
          onBoardingUpdate(
            {
              preferenceId: authState.preferenceId ?? '',
              pastors: true
            },
            {
              onSuccess: () => {
                router.navigate('/(app)/login/nonProfit/onBoarding/totalSalaries');
              }
            }
          );
        },
        onError: (e) => {
          console.error(e);
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <FormWrapper scrollEnable minHeightMinus={160}>
      {/* Height - Header */}
      <View style={styles.container}>
        <OnboardingSection
          label={i18n.t('sign_in.nonProfit.pastors.title')}
          description={i18n.t('sign_in.nonProfit.pastors.description')}>
          {getValues('pastors') && getValues('pastors').length === 0 ? (
            // height - header - footer
            <View style={{ height: height - 160 - bottom - top }}>
              <PastorForm
                initialData={getValues('pastors.0')}
                showDoneButton
                showBackButton
                isLoading={onBoardingIsPending || pastorsPending}
                manualValidation={true}
                onDone={(data, triggerPastor) => {
                  if (data.fullName && data.position) {
                    clearErrors();
                    insert(0, data);
                    handleSubmit(onSubmit)();
                  } else if (!data.fullName || !data.position || data.allowance || data.annualSalary) {
                    triggerPastor();
                  }
                }}
                onAdd={(data) => {
                  clearErrors();
                  update(0, data);
                  trigger('pastors.0').then((isValid) => {
                    if (isValid) {
                      router.navigate('/login/nonProfit/addUpdatePastor');
                    }
                  });
                }}
                doneLabel={i18n.t('mix.next')}
              />
            </View>
          ) : (
            <View style={styles.flexOne}>
              <Divider style={styles.divider} />
              <FlatList
                style={styles.flexOne}
                contentContainerStyle={styles.flexOne}
                scrollEnabled={false}
                data={fields}
                keyExtractor={(pastor) => pastor.id}
                renderItem={(field) => (
                  <FieldArrayListItemCard
                    onPress={() => {
                      router.navigate({
                        pathname: '/login/nonProfit/addUpdatePastor',
                        params: {
                          pastorDataJson: JSON.stringify(field.item),
                          pastorIndex: field.index
                        }
                      });
                    }}
                    onDelete={() => {
                      remove(field.index);
                    }}
                    title={field.item.fullName}
                    subtitle={field.item.position ?? ''}
                    budget={field.item.annualSalary}
                  />
                )}
                ListFooterComponent={
                  <AddAnotherButton
                    onPress={() => router.navigate('/login/nonProfit/addUpdatePastor')}
                    label={i18n.t('sign_in.nonProfit.pastors.add_another')}
                  />
                }
              />
            </View>
          )}
        </OnboardingSection>

        {errors.root && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

        <View style={styles.buttonsContainer}>
          {getValues('pastors') && getValues('pastors').length > 0 && (
            <View style={styles.nextBackContainer}>
              {router.canGoBack() && (
                <BrandedButton
                  disabled={onBoardingIsPending || pastorsPending}
                  style={styles.flexOne}
                  onPress={router.back}>
                  {capitalize(i18n.t('mix.back'))}
                </BrandedButton>
              )}
              <BrandedButton
                style={styles.flexOne}
                loading={onBoardingIsPending || pastorsPending}
                disabled={!isValid && submitCount > 0}
                onPress={handleSubmit(onSubmit)}>
                {i18n.t('mix.next')}
              </BrandedButton>
            </View>
          )}
          <SaveAndContinueLaterButton disabled={onBoardingIsPending || pastorsPending} />
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  buttonsContainer: {
    flexDirection: 'column'
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flex: 1,
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  divider: {
    marginBottom: 15
  }
});

export default Pastors;
